import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { get, qs } from '../api';
import { usePermissions } from '../auth';
import { Empty, ErrorBox, Loading, PageHeader } from '../components/ui';
import { AUDIT_LABEL, fmtDate } from '../format';
import type { AuditEntry } from '../types';
import { groupName, useGroups } from './shared';

const LIMIT = 100;

function describe(e: AuditEntry): string {
  const d = e.details ?? {};
  const parts: string[] = [];
  for (const key of ['email', 'name', 'role', 'fileName', 'prefix']) {
    if (typeof d[key] === 'string') parts.push(String(d[key]));
  }
  if (d.active === false) parts.push('desactivado');
  if (d.active === true) parts.push('activado');
  if (d.passwordReset === true) parts.push('contraseña restablecida');
  return parts.join(' · ');
}

export function AuditPage() {
  const perms = usePermissions();
  const groups = useGroups();
  const [groupId, setGroupId] = useState('');
  const [offset, setOffset] = useState(0);

  const audit = useQuery({
    queryKey: ['audit', { groupId, offset }],
    queryFn: async () => (await get<{ items: AuditEntry[] }>(`/audit${qs({ groupId, offset, limit: LIMIT })}`)).items,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Auditoría" subtitle="Accesos y cambios de administración" />
      <div className="card">
        {perms.isSuper && (
          <div className="toolbar">
            <select className="select" value={groupId} onChange={(e) => (setGroupId(e.target.value), setOffset(0))}>
              <option value="">Todos los grupos</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <ErrorBox error={audit.error} />
        {audit.isLoading ? (
          <Loading />
        ) : !audit.data?.length ? (
          <Empty icon={ScrollText} title="Sin registros" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Detalle</th>
                  {perms.isSuper && <th>Grupo</th>}
                  <th>Origen</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.map((e) => (
                  <tr key={e.id}>
                    <td>{fmtDate(e.createdAt)}</td>
                    <td>
                      <span className={`badge ${e.action === 'auth.login_failed' ? 'badge-failed' : ''}`}>{AUDIT_LABEL[e.action] ?? e.action}</span>
                    </td>
                    <td className="wrap">{describe(e)}</td>
                    {perms.isSuper && <td>{groupName(groups.data, e.groupId)}</td>}
                    <td>
                      {e.userId ? (
                        <span title={e.userEmail ?? undefined}>{e.userName ?? `Usuario #${e.userId} (eliminado)`}</span>
                      ) : e.apiKeyId ? (
                        <span>API key: {e.apiKeyName ?? `#${e.apiKeyId}`}</span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="mono faint">{e.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination">
          <span />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
              Más recientes
            </button>
            <button className="btn btn-sm" disabled={(audit.data?.length ?? 0) < LIMIT} onClick={() => setOffset(offset + LIMIT)}>
              Más antiguos
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
